<script lang="ts">
  import { onMount } from 'svelte';
  import { derived } from 'svelte/store';
  import Router from 'svelte-spa-router';
  import { initI18n } from './lib/i18n';
  import { themeStore } from './lib/stores/themeStore';
  import { costStore, meetingStore } from './lib/stores/meetingStore';
  import { themeSettingsStore } from './lib/stores/themeSettingsStore';
  import { notificationStore } from './lib/stores/notificationStore';
  import { updateFaviconAndLogo, updateAppBadge } from './lib/utils/faviconManager';
  import { initializeManifestManager } from './lib/utils/manifestManager';
  import { showThresholdNotification, canShowNotifications } from './lib/utils/notifications';
  import { speakThresholdWarning, isSpeechSynthesisSupported } from './lib/utils/speechSynthesis';
  import Calculator from './lib/components/Calculator.svelte';
  
  let i18nReady = false;
  
  const routes = {
    '/': Calculator,
  };
  
  // Derived store that combines cost and settings for favicon updates
  const faviconUpdateStore = derived(
    [costStore, themeSettingsStore],
    ([$cost, $settings]) => ({
      cost: $cost,
      yellowThreshold: $settings.yellowThreshold,
      redThreshold: $settings.redThreshold,
    })
  );
  
  // Derived store for badge updates (needs meeting state for isRunning and notification store for badgesEnabled)
  const badgeUpdateStore = derived(
    [costStore, meetingStore, notificationStore],
    ([$cost, $meeting, $notifications]) => ({
      cost: $cost,
      isRunning: $meeting.isRunning,
      badgesEnabled: $notifications.badgesEnabled,
    })
  );
  
  // Subscribe to combined store to update favicon/logo
  faviconUpdateStore.subscribe(({ cost, yellowThreshold, redThreshold }) => {
    updateFaviconAndLogo(cost, yellowThreshold, redThreshold);
  });
  
  // Subscribe to badge store to update app badge
  badgeUpdateStore.subscribe(({ cost, isRunning, badgesEnabled }) => {
    // Only update badge if badges are enabled
    if (badgesEnabled) {
      // Handle async badge update
      updateAppBadge(cost, isRunning).catch((error) => {
        console.warn('Failed to update app badge:', error);
      });
    } else {
      // Clear badge if badges are disabled
      updateAppBadge(0, false).catch((error) => {
        console.warn('Failed to clear app badge:', error);
      });
    }
  });
  
  // Derived store for notification checks
  const notificationCheckStore = derived(
    [costStore, themeSettingsStore, notificationStore, meetingStore],
    ([$cost, $settings, $notifications, $meeting]) => ({
      cost: $cost,
      yellowThreshold: $settings.yellowThreshold,
      redThreshold: $settings.redThreshold,
      notificationsEnabled: $notifications.enabled,
      speechSynthesisEnabled: $notifications.speechSynthesisEnabled,
      yellowNotified: $notifications.yellowNotified,
      redNotified: $notifications.redNotified,
      currency: $meeting.currency,
      isRunning: $meeting.isRunning,
    })
  );
  
  // Track previous cost to detect threshold crossings
  let previousCost = 0;
  let wasRunning = false;
  
  // Subscribe to notification check store
  notificationCheckStore.subscribe(({
    cost,
    yellowThreshold,
    redThreshold,
    notificationsEnabled,
    speechSynthesisEnabled,
    yellowNotified,
    redNotified,
    currency,
    isRunning,
  }) => {
    // Reset tracking when timer is reset (cost goes to 0 while not running)
    if (!isRunning && wasRunning && cost === 0) {
      notificationStore.resetTracking();
      previousCost = 0;
    }
    wasRunning = isRunning;
    
    // Only check thresholds if timer is running
    if (!isRunning) {
      previousCost = cost;
      return;
    }
    
    // Check if we crossed the red threshold (check red first since it's higher)
    if (cost >= redThreshold && !redNotified && previousCost < redThreshold) {
      notificationStore.markRedNotified();
      
      // Show browser notification if enabled
      if (canShowNotifications(notificationsEnabled)) {
        showThresholdNotification('red', cost, currency).catch((error) => {
          console.warn('Failed to show red threshold notification:', error);
        });
      }
      
      // Speak warning if speech synthesis is enabled
      if (speechSynthesisEnabled && isSpeechSynthesisSupported()) {
        speakThresholdWarning('red', cost, currency).catch((error) => {
          console.warn('Failed to speak red threshold warning:', error);
        });
      }
    }
    // Check if we crossed the yellow threshold (only if not already at red)
    else if (cost >= yellowThreshold && !yellowNotified && previousCost < yellowThreshold && cost < redThreshold) {
      notificationStore.markYellowNotified();
      
      // Show browser notification if enabled
      if (canShowNotifications(notificationsEnabled)) {
        showThresholdNotification('yellow', cost, currency).catch((error) => {
          console.warn('Failed to show yellow threshold notification:', error);
        });
      }
      
      // Speak warning if speech synthesis is enabled
      if (speechSynthesisEnabled && isSpeechSynthesisSupported()) {
        speakThresholdWarning('yellow', cost, currency).catch((error) => {
          console.warn('Failed to speak yellow threshold warning:', error);
        });
      }
    }
    
    previousCost = cost;
  });
  
  onMount(async () => {
    await initI18n();
    // Initialize theme on mount
    themeStore.subscribe(() => {});
    // Initialize manifest manager for dynamic theme colors
    initializeManifestManager(themeStore, themeSettingsStore);
    i18nReady = true;
  });
</script>

{#if i18nReady}
  <Router {routes} />
{:else}
  <div class="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900 flex items-center justify-center transition-colors duration-300">
    <div class="text-slate-900 dark:text-white text-xl">Loading...</div>
  </div>
{/if}
